import { isDefined, Mutable } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import { CHOICE_TYPE } from '../../../types/choice';
import { ContentId } from '../../../types/content-block';
import { FormsConstants } from '../../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../../types/functional-element';
import { FunctionalElements } from '../../../types/functional-elements';
import { AstoriaOption, isCosimaOption, OPTION_FUNCTIONAL_ELEMENT_TYPE } from '../../../types/option';
import { RadioOption, RADIO_OPTION_TYPE } from '../../../types/radio-option';
import { RadioOptions } from '../../../types/radio-options';
import { updateAstoriaOption, updateCosimaOption } from '../../actions/functional-elements.actions';
import { FormState } from '../../state/form.state';
import {
    resetFunctionalElement,
    setFunctionalElementToNA,
    setFunctionalElementToYes,
    updateStateAfterContentChanges,
} from './form-reducer.utilities';
import { updateFunctionalElementSelectionReducer } from './selection.reducer';

export function updateAstoriaOptionFunctionalElementReducer(
    state: Readonly<FormState>,
    props: {
        id: ContentId;
        xmlId: string;
        selected: AstoriaOption['selected'];
    },
): Readonly<FormState> {
    // First: Update dependent checkpoints and functional elements
    const optionDependencies = state.optionDependencies[props.id];
    const dependentFunctionalElements: Mutable<FunctionalElements> = {};
    const dependentRadioOptions: Mutable<RadioOptions> = {};
    if (typeof optionDependencies !== 'undefined') {
        const previousSelection = (
            state.participantCopies[state.selectedParticipantCopyId].functionalElements[props.id] as AstoriaOption
        ).selected;
        const checkpointXmlIds =
            typeof optionDependencies !== 'undefined'
                ? optionDependencies.checkpoints.map((id) => state.checkpoints[id].xmlId)
                : [];

        if (props.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO || !isDefined(props.selected)) {
            // If the option is not present, then all dependent elements should be answered with 'N.A.'

            // set radio options of checkpoints to 'N.A.'
            checkpointXmlIds.forEach((xmlId) => {
                const oldOptions = state.participantCopies[state.selectedParticipantCopyId].options[xmlId];
                if (typeof oldOptions === 'undefined') {
                    throw new Error(
                        `FormReducer: updateOptionFunctionalElementReducer: Could not load options for checkpoint '${xmlId}'`,
                    );
                }
                dependentRadioOptions[xmlId] = oldOptions.map(
                    (o: RadioOption): RadioOption => ({
                        ...o,
                        disabled: props.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO, // disable only if the value is set to no. If the value is not defined, then a reset occurred
                        selected:
                            props.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO &&
                            o.optionType === RADIO_OPTION_TYPE.NA,
                    }),
                );
            });

            // set functional elements to 'N.A.'
            const oldFunctionalElements = state.participantCopies[state.selectedParticipantCopyId].functionalElements;
            optionDependencies.functionalElements.forEach((id) => {
                dependentFunctionalElements[id] =
                    props.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO
                        ? setFunctionalElementToNA(oldFunctionalElements[id])
                        : resetFunctionalElement(oldFunctionalElements[id]);

                dependentFunctionalElements[id] = {
                    ...dependentFunctionalElements[id],
                    disabled: props.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO,
                };
            });
        } else if (
            props.selected === OPTION_FUNCTIONAL_ELEMENT_TYPE.YES &&
            (previousSelection === OPTION_FUNCTIONAL_ELEMENT_TYPE.NO || !isDefined(previousSelection))
        ) {
            // All dependent elements should be active and reset.

            // set radio options of checkpoints to 'N.A.'
            checkpointXmlIds.forEach((xmlId) => {
                const oldOptions = state.participantCopies[state.selectedParticipantCopyId].options[xmlId];
                if (typeof oldOptions === 'undefined') {
                    throw new Error(
                        `FormReducer: updateOptionFunctionalElementReducer: Could not load options for checkpoint '${xmlId}'`,
                    );
                }
                dependentRadioOptions[xmlId] = oldOptions.map(
                    (o: RadioOption): RadioOption => ({
                        ...o,
                        disabled: false,
                        selected: false,
                    }),
                );
            });

            const oldFunctionalElements = state.participantCopies[state.selectedParticipantCopyId].functionalElements;
            optionDependencies.functionalElements.forEach(
                (id) => (dependentFunctionalElements[id] = setFunctionalElementToYes(oldFunctionalElements[id])),
            );
        }
    }

    // Second: Create updated option entry
    const newOptionFunctionalElement: { [key: string]: Readonly<AstoriaOption> } = {};
    const oldOptionFunctionalElement = state.participantCopies[state.selectedParticipantCopyId].functionalElements[
        props.id
    ] as AstoriaOption;

    if (oldOptionFunctionalElement.selected === props.selected) {
        return state;
    }

    newOptionFunctionalElement[props.id] = { ...oldOptionFunctionalElement, selected: props.selected };

    // Third: Put everything back together
    return updateStateAfterContentChanges(
        state,
        {
            ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
            ...dependentFunctionalElements,
            ...newOptionFunctionalElement,
        },
        {
            ...state.participantCopies[state.selectedParticipantCopyId].options,
            ...dependentRadioOptions,
        },
    );
}

export function updateCosimaOptionFunctionalElementReducer(
    state: Readonly<FormState>,
    props: {
        id: ContentId;
        selectedChoiceXmlId: string | undefined;
    },
): Readonly<FormState> {
    const optionPresentElement = state.participantCopies[state.selectedParticipantCopyId].functionalElements[props.id];

    const optionPresentChoices = optionPresentElement.choices;
    if (!isDefined(optionPresentChoices)) {
        throw new Error(
            `FormReducer: updateCosimaOptionFunctionalElementReducer: Functional element has no choices attribute!`,
        );
    }

    const selectedChoiceBeforeOptionPresentUpdated = Object.values(optionPresentChoices).find(
        (choice) => choice?.selected,
    );

    let optionSelectedUpdatedState = updateFunctionalElementSelectionReducer(state, props);
    const optionDependencies = optionSelectedUpdatedState.optionDependencies[props.id];

    const dependentFunctionalElementsForOption: Mutable<FunctionalElements> = {};

    const functionalElements =
        optionSelectedUpdatedState.participantCopies[optionSelectedUpdatedState.selectedParticipantCopyId]
            .functionalElements;

    // update
    if (isDefined(optionDependencies)) {
        const optionPresentFunctionalElement =
            optionSelectedUpdatedState.participantCopies[optionSelectedUpdatedState.selectedParticipantCopyId]
                .functionalElements[props.id];

        if (!isDefined(optionPresentFunctionalElement.choices)) {
            throw new Error(
                `FormReducer: updateCosimaOptionFunctionalElementReducer: Functional element has no new choices attribute!`,
            );
        }
        const selectedChoiceForOptionPresent = Object.values(optionPresentFunctionalElement.choices).find(
            (choice) => choice?.xmlId === props.selectedChoiceXmlId,
        );

        const noOptionSelected = selectedChoiceForOptionPresent?.type === CHOICE_TYPE.CHOICE_NEGATIVE;
        const yesOptionSelected = selectedChoiceForOptionPresent?.type === CHOICE_TYPE.CHOICE_POSITIVE;
        const yesOptionSelectedAfterPreviousNoOptionSelected =
            (yesOptionSelected || !isDefined(props.selectedChoiceXmlId)) &&
            selectedChoiceBeforeOptionPresentUpdated?.type === CHOICE_TYPE.CHOICE_NEGATIVE;

        const isMasterCopySelected = state.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID;

        optionDependencies.functionalElements.forEach((id) => {
            const updatedFunctionalElement = { ...functionalElements[id], disabled: noOptionSelected };
            if (isDefined(updatedFunctionalElement.choices)) {
                // update the choices of the depending functional elements
                if (isDefined(updatedFunctionalElement.choices.choiceNa)) {
                    updatedFunctionalElement.choices = {
                        ...updatedFunctionalElement.choices,
                        choiceNa: {
                            ...updatedFunctionalElement.choices.choiceNa,
                            selected: noOptionSelected,
                            selectedInMaster: isMasterCopySelected
                                ? noOptionSelected
                                : updatedFunctionalElement.choices.choiceNa.selectedInMaster,
                        },
                    };
                }

                if (isDefined(updatedFunctionalElement.choices.choiceNegative)) {
                    updatedFunctionalElement.choices = {
                        ...updatedFunctionalElement.choices,
                        choiceNegative: {
                            ...updatedFunctionalElement.choices.choiceNegative,
                            // for depending option present elements select yes
                            selected:
                                updatedFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT &&
                                noOptionSelected,
                            selectedInMaster: isMasterCopySelected
                                ? updatedFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT &&
                                  noOptionSelected
                                : updatedFunctionalElement.choices.choiceNegative.selectedInMaster,
                        },
                    };
                }

                if (isDefined(updatedFunctionalElement.choices.choicePositive)) {
                    updatedFunctionalElement.choices = {
                        ...updatedFunctionalElement.choices,
                        choicePositive: {
                            ...updatedFunctionalElement.choices.choicePositive,
                            selected: false,
                            selectedInMaster: isMasterCopySelected
                                ? false
                                : updatedFunctionalElement.choices.choicePositive.selectedInMaster,
                        },
                    };
                }
            }

            // set the values to the functional element value fields
            if (noOptionSelected) {
                dependentFunctionalElementsForOption[id] = setFunctionalElementToNA(updatedFunctionalElement);
            } else if (yesOptionSelectedAfterPreviousNoOptionSelected) {
                dependentFunctionalElementsForOption[id] = setFunctionalElementToYes(updatedFunctionalElement);
            } else if (!isDefined(selectedChoiceForOptionPresent)) {
                dependentFunctionalElementsForOption[id] = resetFunctionalElement(updatedFunctionalElement);
            } else {
                dependentFunctionalElementsForOption[id] = updatedFunctionalElement;
            }

            if (isCosimaOption(updatedFunctionalElement)) {
                const updatedState = updateStateAfterContentChanges(optionSelectedUpdatedState, {
                    ...optionSelectedUpdatedState.participantCopies[state.selectedParticipantCopyId].functionalElements,
                    ...dependentFunctionalElementsForOption,
                });
                const selectedChoiceXMLId = Object.values(updatedFunctionalElement.choices).find(
                    (c) => c?.selected,
                )?.xmlId;
                optionSelectedUpdatedState = updateCosimaOptionFunctionalElementReducer(updatedState, {
                    id: updatedFunctionalElement.id,
                    selectedChoiceXmlId: selectedChoiceXMLId,
                });
            }
        });
    }

    // Third: Put everything back together
    return updateStateAfterContentChanges(optionSelectedUpdatedState, {
        ...optionSelectedUpdatedState.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ...dependentFunctionalElementsForOption,
    });
}

export const optionReducer = [
    on<FormState, Array<typeof updateAstoriaOption>>(updateAstoriaOption, updateAstoriaOptionFunctionalElementReducer),
    on<FormState, Array<typeof updateCosimaOption>>(updateCosimaOption, updateCosimaOptionFunctionalElementReducer),
];
